import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Button, Modal, FormField, Empty, Alert, Loading, Badge, Table, StatCard } from '../components/ui';
import { Plus, Trash2, Link as LinkIcon, RadioReceiver, Send, Pencil } from 'lucide-react';
import API from '../services/api';

const emptyForm = {
  name: '',
  type: 'outgoing',
  url: '',
  events: 'project.created'
};

export default function Webhooks() {
  const { user } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const response = await API.get('/webhooks');
      setWebhooks(response.data);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setModal(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      name: w.name,
      type: w.type,
      url: w.url || '',
      events: w.events ? w.events.join(', ') : '',
      isActive: w.isActive
    });
    setError('');
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const payload = {
        ...form,
        events: typeof form.events === 'string' ? form.events.split(',').map(e => e.trim()) : form.events
      };
      
      if (editing) {
        await API.put(`/webhooks/${editing.webhook_id}`, payload);
      } else {
        await API.post('/webhooks', payload);
      }
      setModal(false);
      fetchWebhooks();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      try {
        await API.delete(`/webhooks/${id}`);
        fetchWebhooks();
      } catch (error) {
        console.error('Error deleting webhook:', error);
      }
    }
  };

  if (user?.role !== 'Admin') {
    return (
      <div className="animate-in" style={{ paddingBottom: 40 }}>
        <PageHeader title="Access Denied" subtitle="Only administrators can manage webhooks" />
      </div>
    );
  }

  const activeWebhooks = webhooks.filter(w => w.isActive).length;
  const outgoingCount = webhooks.filter(w => w.type === 'outgoing').length;
  const incomingCount = webhooks.filter(w => w.type === 'incoming').length;

  return (
    <div className="animate-in" style={{ paddingBottom: 40 }}>
      <PageHeader 
        title="Webhooks" 
        subtitle="Manage incoming and outgoing webhooks for integration with external services" 
        eyebrow="Settings" 
      />

      {/* Stats Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Webhooks" value={webhooks.length} icon={LinkIcon} />
        <StatCard label="Active" value={activeWebhooks} icon={RadioReceiver} trend="System Status" />
        <StatCard label="Outgoing / Incoming" value={`${outgoingCount} / ${incomingCount}`} icon={Send} />
      </div>

      {/* Toolbar */}
      <Card style={{ marginBottom: 24, padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={openCreate} style={{ padding: '10px 20px' }}>
          <Plus size={16} style={{ marginRight: 8 }} /> New Webhook
        </Button>
      </Card>

      {loading ? (
        <Loading message="Loading webhooks..." />
      ) : webhooks.length === 0 ? (
        <Empty message="No webhooks configured yet." />
      ) : (
        <Card>
          <Table 
            columns={[
              { key: 'name', label: 'Webhook Name', render: r => (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--accent)' }}>{r.name}</div>
                </div>
              )},
              { key: 'type', label: 'Type', render: r => (
                <span style={{ 
                  background: r.type === 'incoming' ? 'rgba(52,211,153,0.12)' : 'rgba(96,165,250,0.12)', 
                  color: r.type === 'incoming' ? '#34d399' : '#60a5fa', 
                  border: `1px solid ${r.type === 'incoming' ? 'rgba(52,211,153,0.25)' : 'rgba(96,165,250,0.25)'}`,
                  padding: '2px 8px', borderRadius: '12px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase'
                }}>
                  {r.type}
                </span>
              )},
              { key: 'endpoint', label: 'URL / Endpoint', render: r => (
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-muted)', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.type === 'outgoing' ? r.url : `/api/webhooks/incoming/${r.endpoint}`}
                </div>
              )},
              { key: 'events', label: 'Events', render: r => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(r.events || []).map(ev => (
                    <span key={ev} style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                      {ev}
                    </span>
                  ))}
                </div>
              )},
              { key: 'status', label: 'Status', render: r => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.isActive ? '#40c057' : '#fa5252' }} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              )},
              { key: 'actions', label: '', render: r => (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(r); }}><Pencil size={13} /></Button>
                  <Button size="sm" variant="danger" onClick={(e) => { e.stopPropagation(); handleDelete(r.webhook_id); }}><Trash2 size={13} /></Button>
                </div>
              )}
            ]}
            data={webhooks}
          />
        </Card>
      )}

      {/* Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Webhook' : 'New Webhook'} width={600}>
        <Alert type="error" message={error} />
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            <FormField label="Webhook Name">
              <input value={form.name} name="name" onChange={handleInputChange} required placeholder="e.g. Slack Integration" className="ui-input" />
            </FormField>
            
            <FormField label="Type">
              <select name="type" value={form.type} onChange={handleInputChange} className="ui-input" disabled={!!editing}>
                <option value="outgoing">Outgoing (Send data to URL)</option>
                <option value="incoming">Incoming (Receive data here)</option>
              </select>
            </FormField>
            
            {form.type === 'outgoing' && (
              <FormField label="URL (Destination)">
                <input type="url" name="url" value={form.url} onChange={handleInputChange} required placeholder="https://api.example.com/webhook" className="ui-input" />
              </FormField>
            )}
            
            <FormField label="Events (comma separated)">
              <input type="text" name="events" value={form.events} onChange={handleInputChange} placeholder="e.g. project.created, task.updated" className="ui-input" />
            </FormField>
            
            {editing && (
              <FormField label="Status">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input type="checkbox" id="isActive" name="isActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} style={{ width: 16, height: 16 }} />
                  <label htmlFor="isActive" style={{ fontSize: 14, color: 'var(--text)' }}>Active (enable this webhook)</label>
                </div>
              </FormField>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 32 }}>
            <Button variant="secondary" onClick={() => setModal(false)} type="button" style={{ padding: '10px 24px' }}>Cancel</Button>
            <Button type="submit" disabled={saving} style={{ padding: '10px 24px' }}>{saving ? 'Saving...' : editing ? 'Update Webhook' : 'Create Webhook'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
